import { lazy, Suspense, useMemo } from 'react';
import { Badge } from '@/common/components/ui/badge';
import { Skeleton } from '@/common/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { CHART_HEIGHT_PX, RESOLUTION_BUCKET_MS } from '@/features/telemetry/config';
import type {
  TelemetryDeviceSeries,
  TelemetryField,
  TelemetryResolution,
} from '@/features/telemetry/services/schemas';
import { toDeviceMetrics } from '@/features/telemetry/mappers/toDeviceMetrics';
import { toChartPoints } from '@/features/telemetry/mappers/toChartPoints';
import { toStatusSegments } from '@/features/telemetry/mappers/toStatusSegments';
import { StatusTimeline } from '@/features/telemetry/components/status-timeline/StatusTimeline';

// recharts (~150KB gzipped) is code-split out of the main bundle, the same way
// LocationMap keeps leaflet out — see LocationPicker.tsx for the established pattern.
const DeviceChart = lazy(() => import('@/features/telemetry/components/device-chart/DeviceChart'));

export interface DeviceCardProps {
  device: TelemetryDeviceSeries;
  fields: TelemetryField[];
  from: Date;
  to: Date;
  resolution: TelemetryResolution;
}

/**
 * One device's monitoring card: a status dot + name + type badge header, a time-series
 * chart, and a status timeline — assembled entirely from the field catalog and this
 * device's own telemetry, via the pure mappers in `mappers/`.
 *
 * The API's `Devices` resource carries no health/status field, so the status dot is
 * derived from data presence in the selected window rather than a real device state —
 * see the plan's gap notes. It answers "did this device report anything in this
 * range?", not "is it healthy right now?".
 */
export function DeviceCard({ device, fields, from, to, resolution }: DeviceCardProps) {
  const bucketMs = RESOLUTION_BUCKET_MS[resolution];

  const metrics = useMemo(
    () => toDeviceMetrics(fields, device.deviceType, resolution),
    [fields, device.deviceType, resolution]
  );

  const points = useMemo(
    () => toChartPoints(device.series, metrics.series),
    [device.series, metrics.series]
  );

  const channels = useMemo(
    () =>
      metrics.channels.map((channel) => ({
        key: channel.key,
        label: channel.label,
        segments: toStatusSegments(device.series, channel.key, from, to, bucketMs),
      })),
    [metrics.channels, device.series, from, to, bucketMs]
  );

  const hasData = device.series.length > 0;

  return (
    <div
      data-testid="device-card"
      className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'size-2.5 shrink-0 rounded-full',
              hasData ? 'bg-emerald-500' : 'bg-muted-foreground/40'
            )}
            aria-hidden="true"
          />
          <span className="truncate text-sm font-semibold text-foreground">
            {device.deviceName}
          </span>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {device.deviceType}
        </Badge>
      </div>
      <Suspense fallback={<Skeleton className="rounded-lg" style={{ height: CHART_HEIGHT_PX }} />}>
        <DeviceChart points={points} series={metrics.series} from={from} to={to} />
      </Suspense>
      <StatusTimeline channels={channels} from={from} to={to} />
    </div>
  );
}
